//* Vue
import type { SetupContext } from "vue";
//* 组件库
import type { UiTooltipFunction, UiSimplebar } from "various-ui";
import { $message } from "various-ui";
//* 私有组件
import type ModuleCodonContrast from "../../codon-contrast/index.vue";
import type ModuleInfo from "../components/info/index.vue";
//* 组件属性
import type { DrawInitData, DrawEmitsOption } from "./types";
import Example from "../index";
//* 样式
import css from "../index.module.less";

export const useComposable = (emits: SetupContext<typeof DrawEmitsOption>["emit"]) => {
    //* 插件
    const i18n = useI18n();

    //* 静态变量
    const vars: { example?: Example } = {};

    //* 响应式属性
    const refs = {
        view: Example.view,
        area: Example.view_area,
        mode: ref<string>(""),
        size: ref<number>(800),
        sequence: ref<string>(""),
    };

    //* 节点
    const nodes = {
        tips: ref<InstanceType<typeof UiTooltipFunction>>(),
        info: ref<InstanceType<typeof ModuleInfo>>(),
        tooltip: ref<HTMLDivElement>(),
        container: ref<HTMLDivElement>(),
        comparison: ref<InstanceType<typeof ModuleCodonContrast>>(),
        simplebar: ref<InstanceType<typeof UiSimplebar>>(),
    };

    //* 函数列表
    const methods = {
        //* 定位函数
        move: (site: number, tips?: boolean) => vars.example?.move(site, tips),
        //* 外显子区域
        exons: () => vars.example?.exons || [],
        //* 渲染数据更新
        update: (options: { radius?: number[][]; selects?: string[]; tips?: boolean; area?: string; site?: number; info?: boolean }) =>
            vars.example?.update(options),
        //* 渲染函数
        render: (mode: string, data: DrawInitData, setup?: (data: any) => void) => {
            //* 关闭密码子表
            nodes.comparison.value?.close();
            //* 关闭信息弹窗
            nodes.info.value?.close();
            nodes.simplebar.value?.$el.querySelector(".ui-simplebar-container")?.scrollTo({ top: 0 });
            //* 实例化绘图对象
            if (!nodes.tips.value || !nodes.info.value || !nodes.container.value || !nodes.simplebar.value) $message.error(i18n.t("UnknownError"));
            else {
                refs.size.value = data.size;
                refs.mode.value = mode;
                vars.example = new Example(mode, data, {
                    info: nodes.info.value,
                    tips: nodes.tips.value,
                    setup: setup,
                    container: nodes.container.value,
                    simplebar: nodes.simplebar.value,
                });
            }
        },

        //* 页面滚动事件
        scroll: () => {
            //* 更新视图
            vars.example?.render();
            //* 隐藏Tips窗口
            nodes.tips.value?.hidden();
        },

        //* 序列获取函数
        sequence: () => vars.example?.sequence || "",
        cds: () => vars.example?.cds || "",
        radius: () => vars.example?.radius || "",

        //* 序列的突变详情
        mutations: () => {
            if (refs.view.value?.mutations?.count) {
                //* 数据初始化
                const result = {
                    ids: <number[]>[],
                    mrna: "",
                    name: refs.view.value.data?.name || refs.view.value.data?.char,
                    site: refs.view.value.data?.site,
                    codon: refs.view.value.data?.codon,
                    data: Object.assign(computeds.params.value, {
                        is: !refs.view.value?.data?.codons?.find((value: any) => value.area != refs.view.value?.data?.area),
                    }),
                };

                //* 突变id数据获取
                for (const i in refs.view.value.mutations.data) {
                    refs.view.value.mutations.data[i].forEach((value: any) => result.ids.push(value.id));
                }

                return emits("info", result);
            }
        },

        //* 预测函数
        next: (mode: "RNA-Splicer" | "Pathogenicity-Predictor") => {
            emits("next", Object.assign(computeds.params.value, { mode: mode }));
        },
    };

    //* 计算属性
    const computeds = {
        params: computed(() => {
            //* 数据初始化
            const cds = refs.view.value?.data?.sites?.cds || "";
            const char = refs.view.value?.data?.char || "";
            const site = refs.view.value?.data?.site || "";
            const result = { cds: "", site: site, carry: false };
            //* 检测是否需要携带检索的突变后碱基
            if (vars.example?.radius.length == 1 && vars.example.radius[0]?.[0] == vars.example.radius[0]?.[1]) {
                result.carry = refs.view.value?.data.site == vars.example.radius[0][0];
            }

            //* 蛋白与转录本处理
            if (cds && char && site) {
                if (vars.example?.mode == "codon") result.cds = `p.${char}${site}`;
                else {
                    result.cds = `c.${cds}${char}`;
                }
            }
            return result;
        }),
    };

    //* 动态属性
    const binds = reactive({
        tips: computed(() => {
            return { "trigger": "none" as "none", "z-index": 666, "offset-x": 43, "offset-y": 0, "class-extra-name": css.container };
        }),
    });

    //* 响应事件
    const ons = {
        body: {
            mouseenter: () => Example.show?.(),
            mouseleave: () => Example.hidden?.(),
        },
    };

    return { vars, ons, refs, nodes, binds, methods, computeds };
};
